import React, { useEffect, useState } from 'react'

import { Col, Result, Skeleton, Spin, Typography, Button } from 'antd'
import Meta from 'antd/lib/card/Meta'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import noop from 'lodash/noop'
import omit from 'lodash/omit'
import truncate from 'lodash/truncate'

import { TREND_CATEGORIES } from '../..'
import { useOnUpdate } from '../../../../../../../hooks/useOnUpdate'
import usePrevious from '../../../../../../../hooks/usePrevious'
import { pickWithDefaults } from '../../../../../../../utils/data/utils'
import { capitalize } from '../../../../../../../utils/stringManipulation'
import HiringTrendsChart from '../../../../components/HiringTrendsChart'
import { useGraduateOutcomesTrendsData } from '../../../../hooks/useGraduateOutcomesTrendsData'
import { useGraduateOutcomesTrendsOptions } from '../../../../hooks/useGraduateOutcomesTrendsOptions'
import { useCurrentlyLoaded } from '../../../hooks/useCurrentlyLoaded'
import TrendsCard from '../TrendsCard'

const { Text } = Typography
const headingStyles = { marginBottom: 0 }
const titleId = 'job-functions-heading'
const descriptionId = 'job-functions-description'
const chartTitleId = 'job-functions-hiring-trends-heading'
const chartDescriptionId = 'job-functions-hiring-trends-description'

export default function JobTrends(props) {
  const {
    clientName,
    finalTaxonomy,
    loadingTopLevelData,
    topLevelFilters,
    universitiesToSubmit,
    universityNameDisplay,
    isActiveTab = true,
  } = pickWithDefaults(props, {
    clientName: null,
    finalTaxonomy: '',
    isActiveTab: true,
    loadingTopLevelData: false,
    setShowingLoaderForTrendsTab: noop,
    showingLoaderForTrendsTab: false,
    topLevelFilters: {},
    universitiesToSubmit: [],
    universityNameDisplay: '',
  })

  const [selectedJobFunctionOption, setSelectedJobFunctionOption] = useState(null)
  const [loadingInitialTrends, setLoadingInitialTrends] = useState(true)
  const {
    loadingGraduateOutcomesTrendsOptions,
    graduateOutcomesTrendOptionsError,
    trendOptions,
    docCount,
  } = useGraduateOutcomesTrendsOptions({
    clientName,
    finalTaxonomy,
    topLevelFilters,
    trendCategory: TREND_CATEGORIES.JOB_FUNCTIONS,
    universitiesToSubmit,
  })

  const topJobFunctions = get(trendOptions, 'top_job_categories', [])

  const {
    getTrendsData,
    loadingTrendsData,
    trendsData,
    trendsDataError,
  } = useGraduateOutcomesTrendsData({
    setLoadingInitialTrends,
  })

  useOnUpdate(topJobFunctions, () => {
    if (topJobFunctions.length) {
      const firstOption = topJobFunctions[0]
      const topJobFunctionsName = get(firstOption, 'key')
      setSelectedJobFunctionOption(topJobFunctionsName)
    }
  })

  const filters = {
    ...omit(topLevelFilters, 'universities'),
    taxonomy: finalTaxonomy,
    universities: universitiesToSubmit,
  }
  const prevSelectedJobFunctionOption = usePrevious(selectedJobFunctionOption)
  const prevFilters = usePrevious(filters)
  useEffect(() => {
    if (
      selectedJobFunctionOption &&
      (!isEqual(prevSelectedJobFunctionOption, selectedJobFunctionOption) ||
        !isEqual(prevFilters, filters))
    ) {
      getTrendsData({
        variables: {
          category: TREND_CATEGORIES.JOB_FUNCTIONS,
          filters,
          targets: [selectedJobFunctionOption],
        },
      })
    }
  }, [
    filters,
    getTrendsData,
    prevFilters,
    prevSelectedJobFunctionOption,
    selectedJobFunctionOption,
  ])

  const handleJobFunctionClick = value => {
    if (loadingTrendsData) {
      return
    } else {
      if (value === selectedJobFunctionOption) setSelectedJobFunctionOption(null)
      else setSelectedJobFunctionOption(value)
    }
  }

  const jobTrends = get(trendsData, 'job_functions_hiring_trends', [])
  const loadedJobTrends = useCurrentlyLoaded(jobTrends)

  const loadingAll =
    loadingTopLevelData || loadingGraduateOutcomesTrendsOptions || loadingTrendsData
  const hasError = trendsDataError || graduateOutcomesTrendOptionsError
  const noResults = (!loadingAll && !topJobFunctions.length && !jobTrends.length) || hasError
  const lowVolume = !loadingAll && docCount < 100

  return (
    <TrendsCard dataTestId="job-functions">
      <Col md={8} role="region" aria-labelledby={titleId} aria-describedby={descriptionId}>
        <Meta
          title={
            <h2 id={titleId} style={headingStyles}>
              Job Functions
            </h2>
          }
          description={
            <span id={descriptionId}>
              Click any job sector to display hiring trends on the right.
            </span>
          }
        />
        <div className="click-list" data-testid="job-functions-list">
          <Skeleton
            loading={loadingGraduateOutcomesTrendsOptions}
            paragraph={{ rows: 12 }}
            title={false}
          >
            {topJobFunctions.map((option, index) => {
              const { key: name } = pickWithDefaults(option, {
                key: '',
              })
              const isSelected = selectedJobFunctionOption === name
              const backgroundColor = isSelected
                ? 'rgb(80, 153, 222, 0.5)'
                : !name && index === 0
                ? 'rgb(80, 153, 222, 0.5)'
                : ''
              const nameDisplay =
                isSelected && loadingTrendsData ? (
                  <div style={{ height: 20, marginLeft: 8, opacity: 0.8 }}>
                    <Spin size="small" />
                  </div>
                ) : (
                  <div style={{ float: 'left' }}>
                    <Text>{capitalize(name)}</Text>
                  </div>
                )

              return (
                <Button
                  key={`${name}${index}`}
                  type="link"
                  className="click-list-item"
                  onClick={() => handleJobFunctionClick(name)}
                  disabled={!isActiveTab || loadingTrendsData}
                  tabIndex={isActiveTab ? 0 : -1}
                  aria-label={`Select ${capitalize(name)} job function`}
                  style={{
                    backgroundColor,
                    border: 'none',
                    clear: 'both',
                    height: 'auto',
                    padding: '8px 16px',
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  <div>{nameDisplay}</div>
                </Button>
              )
            })}
          </Skeleton>
        </div>
      </Col>

      <Col
        md={16}
        role="region"
        aria-labelledby={chartTitleId}
        aria-describedby={chartDescriptionId}
      >
        <Meta
          title={
            <h3 id={chartTitleId} style={headingStyles}>
              Job Functions Hiring Trends
            </h3>
          }
          description={
            <span id={chartDescriptionId}>
              The job functions of your graduates, as a percentage of the total graduates. (20 year
              trend)
            </span>
          }
        />
        {lowVolume ? (
          <Result
            title="Sample size is too small for trend analysis."
            subTitle="Please expand your filter criteria to show trends."
          />
        ) : noResults ? (
          <Result
            title="Sorry, no data available with current filters."
            subTitle="Please try clearing some filters."
          />
        ) : (
          <Spin spinning={loadingInitialTrends}>
            <HiringTrendsChart
              trends={loadedJobTrends}
              referenceKey={truncate(universityNameDisplay, {
                length: 66,
              })}
            />
          </Spin>
        )}
      </Col>
    </TrendsCard>
  )
}
